import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Lightbulb, Clock, HelpCircle, Bookmark, Tag, Sparkles, Brain } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { SkeletonCard } from "@/components/Skeletons";
import api from "@/lib/api";
import { useT } from "@/i18n";

const ICONS = { topic: Tag, duration: Clock, hook: HelpCircle, time: Clock, saves: Bookmark };

export function Insights() {
  const { t } = useT();
  const { data, isLoading } = useQuery({
    queryKey: ["insights"],
    queryFn: async () => (await api.get("/insights")).data,
    retry: false,
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("insights.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("insights.subtitle")}</p>
        </div>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2">{[0,1,2,3].map(i => <SkeletonCard key={i} className="h-32" />)}</div>
        ) : !data?.enough_data ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="insights-learning">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10">
              <Brain className="h-7 w-7 text-brand-500" />
            </div>
            <h3 className="font-display text-lg font-semibold">{t("insights.learning")}</h3>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">{data?.message}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{t("insights.reelsAnalyzed", { n: data.reels_analyzed })} · {t("insights.computedNote")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {data.insights.map((ins, i) => {
                const Icon = ICONS[ins.type] || Lightbulb;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                    className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid={`insight-${i}`}>
                    <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-brand-500/10 blur-lg" />
                    <div className="relative flex items-start gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium leading-snug">{ins.text}</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs ${ins.confidence === "alta" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : ins.confidence === "media" ? "bg-amber-500/10 text-amber-700 dark:text-amber-300" : "bg-muted text-muted-foreground"}`} data-testid={`insight-confidence-${i}`}>
                            {t("insights.confidence")}: {ins.confidence}
                          </span>
                          {ins.sample != null && (
                            <span className="inline-block rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground" data-testid={`insight-sample-${i}`}>
                              {t("insights.sample", { n: ins.sample })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
